import { KiwixiService } from './../../kiwixi/kiwixi.service';
import { AfterViewInit, Component, Input, OnInit, inject } from '@angular/core';
import moment from 'moment';
import { TranslateService } from '@ngx-translate/core';
import { Lilate } from '../../model/lilate.model';
import { Router } from '@angular/router';
import { MaterialService } from '../../service/material.service';
import { environment } from '../../../../../environments/environment';
import { ExaminationService } from '../../service/examination.service';
import { SharedAppState } from '../../store/app.states';
import { Store } from '@ngrx/store';
import { Subject, takeUntil } from 'rxjs';
import { ExaminationSessionService } from '../../service/examination-session.service';
import { canStartLilate, getLilateBlockedMessage } from '../../utils/bundle-flashlevel.util';
import { isLilateCancellationLocked } from '../../utils/lilate-cancellation.util';
import { EXAMINATION_ROOM_OPENING_MINUTES_BEFORE_START } from '../../utils/examination-room-opening.util';
import { ServerClockService } from '../../service/server-clock.service';
import { isBeforeLilateStart, lilateStartShortLabel } from '../../utils/lilate-start-date.util';

@Component({
  selector: 'app-lilate-card',
  templateUrl: './lilate-card.component.html',
  styleUrls: ['./lilate-card.component.scss']
})
export class LilateCardComponent implements OnInit, AfterViewInit {
  @Input() lilate: Lilate;
  isPendingAssessment = false;
  // Heure du serveur, recalée par la page « à venir » à la réception de la liste :
  // l'horloge du poste peut être décalée (SUP-2371)
  private serverClock = inject(ServerClockService);
  now = this.serverClock.nowMoment();
  providerRequirements: boolean = false;
  duration: any;
  durationMs: number;
  countDownLabelHours: string;
  countDownLabelDays: string;
  countDownLabelDay: string;
  countDownLabelMinutes: string;
  countDownLabelSeconds: string;
  countdown: any = null;
  countdownStatus = 'in'

  isStepBirthdateDone = false;
  isStepPhotoDone = false;
  isStepBusinessDone = false;
  isStepLevelDone = false;

  // Mapping CECR pour comparer les niveaux
  cecrMapping: { [key: number]: string } = {
    1: 'A1',
    2: 'A2',
    3: 'B1',
    4: 'B2',
    5: 'C1',
    6: 'C2'
  };

  private _unsubscribeAll: Subject<any> = new Subject<any>();
  me;

  constructor(
    public _router: Router,
    private materialService: MaterialService,
    private translateService: TranslateService,
    private kiwixiService: KiwixiService,
    private examinationService: ExaminationService,
    private store: Store<SharedAppState>
  ) {
    this.setCountDownTranslation();
  }


  ngOnInit() {
    this.isStepBirthdateDone = this.lilate.student.user.birthdate ? true : false;
    // if (this.lilate.job_situation != null
    //   && ((((this.lilate.business || this.lilate.business_suggested)) && ((this.lilate.job || this.lilate.job_suggested))) ||
    //     (this.lilate.job_situation === 'retired' || this.lilate.job_situation === 'student'))
    // )

    this.isStepPhotoDone = this.lilate.student.user.photo_profile ? true : false;
    this.isStepLevelDone = this.lilate.targeted_level ? true : false;

    this.store.select('user').pipe(takeUntil(this._unsubscribeAll)).subscribe(
      store => {
        this.me = store.me;

        if (this.me.user.job_situation != null
          && ((((this.me.user.business)) && ((this.me.user.job))) ||
            (this.me.user.job_situation === 'retired' || this.me.user.job_situation === 'student'))
        ) {
            this.isStepBusinessDone = true;
        }
      }
    )

    this.setCountdown();
    setInterval(() => this.setCountdown(), 1000);
    this.checkIfPendingAssessment();
    setInterval(() => this.checkProviderRequirements(), 30000)
  }

  ngAfterViewInit(): void {
    this.checkProviderRequirements();
  }

  ngOnDestroy(): void {
    // Unsubscribe from all subscriptions
    this._unsubscribeAll.next(null);
    this._unsubscribeAll.complete();
  }

  getCurrentLanguage() {
    return this.kiwixiService.getCurrentLanguage();
  }

  bookLilate(lilate: Lilate): void {
    if (!canStartLilate(lilate)) {
      this.materialService.openSnackBar(
        getLilateBlockedMessage(lilate.path) || 'Vous devez passer votre FlashLevel avant de passer votre LILATE.',
        'orange',
      );
      return;
    }
    // Réservation possible dès l'inscription : le calendrier ne propose que des
    // créneaux à partir de la date de début (contrôlé par Engage).
    this._router.navigateByUrl('booking', { state: { data: { lilate: lilate, booking: true } } });
  }

  isLilateBundleBlocked(): boolean {
    return !canStartLilate(this.lilate);
  }

  /** True tant que la date de début du passage est future (jour de Paris) : la carte l'annonce. */
  isBeforeEarliestStartDate(): boolean {
    return isBeforeLilateStart(this.lilate);
  }

  /** Date de début du passage (premier jour des créneaux proposés) au format JJ/MM. */
  earliestStartDateLabel(): string {
    return lilateStartShortLabel(this.lilate);
  }

  getLilateBundleBlockedMessage(): string | null {
    return getLilateBlockedMessage(this.lilate?.path);
  }

  checkIfPendingAssessment() {
    this.isPendingAssessment = moment(this.lilate.end).isBefore(this.now) ? true : false;
  }

  /**
   * Vérifie si le passage n'est plus annulable ni reportable (à moins de 24 heures)
   */
  isCancellationLocked(): boolean {
    return isLilateCancellationLocked(this.lilate?.start);
  }

  /**
   * Vérifie si on est à moins d'1 heure du début de la session
   * @returns true si moins d'1h avant le début
   */
  isLessThan1Hour(): boolean {
    const diff = moment(this.lilate.start).diff(this.now);
    const duration = moment.duration(diff);
    return duration.asHours() < 1;
  }

  isLilateAvailable() {
    return moment(this.lilate.start).add(this.lilate.minimum_access_time, 'days').isSameOrBefore(moment(), 'day');
  }

  changeDateLilate(lilate: Lilate): void {
    if (!canStartLilate(lilate)) {
      this.materialService.openSnackBar(
        getLilateBlockedMessage(lilate.path) || 'Vous devez passer votre FlashLevel avant de passer votre LILATE.',
        'orange',
      );
      return;
    }
    if (this.isCancellationLocked()) {
      this.materialService.openSnackBar("Impossible de changer la date de votre examen à moins de 24 heures du passage", 'red')
    } else {
      this._router.navigateByUrl('booking', { state: { data: { lilate: lilate, booking: false } } });
    }
  }

  nowIsafter() {
    return this.now.isAfter(this.lilate.end)
  }

  checkProviderRequirements() {
    if (this.lilate) {
      if (this.lilate.provider_link) {
        // le bouton s'active à l'ouverture de la salle (10 min avant) et reste actif pendant
        // tout le passage, à l'heure du serveur (EXAMINATION-R9)
        const opening = moment(this.lilate.start).subtract(EXAMINATION_ROOM_OPENING_MINUTES_BEFORE_START, 'minutes');
        if (this.serverClock.nowMoment().isBetween(opening, moment(this.lilate.end))) {
          this.providerRequirements = true;
        } else {
          this.providerRequirements = false;
        }
      }
    }
  }

  openTeamsLink() {
    if (this.lilate.provider_link) {
      if (this.lilate.examination_version === '1') {
        window.open(this.lilate.provider_link, '_blank');
      } else if (this.lilate.examination_version === '2') {
        this.examinationService.openSession(this.me.user.email, this.lilate.id, localStorage.getItem('access_token'), '_self');
      }
    }
  }

  openSetupMedia() {
    window.open(environment.urls.exam + '/audio-video-test', '_blank');
  }

  showCsatForm() {
    const url = "/donner-votre-avis?slug=csat-hot&lilate_id=" + this.lilate.id + "&token=" + this.lilate.token_poll + "&go_to=/certificates";
    this._router.navigateByUrl(url);
  }

  openCertificateLink() {
    if (this.lilate.is_satisfaction_form_responded && this.lilate.token_certificate) {
      const url = environment.urls.lilate + '/lilate/' + this.lilate.token_certificate;
      window.open(url, '_blank');
    } else {
      this.showCsatForm();
    }
  }


  setCountDownTranslation(refreshCountDown = false) {
    this.translateService.stream('common.jour').subscribe((text: string) => { this.countDownLabelDay = text; if (refreshCountDown) { this.setCountdown() } });
    this.translateService.stream('common.jours').subscribe((text: string) => { this.countDownLabelDays = text; if (refreshCountDown) { this.setCountdown() } });
    this.translateService.stream('common.hours').subscribe((text: string) => { this.countDownLabelHours = text; if (refreshCountDown) { this.setCountdown() } });
    this.translateService.stream('common.minutes').subscribe((text: string) => { this.countDownLabelMinutes = text; if (refreshCountDown) { this.setCountdown() } });
    this.translateService.stream('common.seconds').subscribe((text: string) => { this.countDownLabelSeconds = text; if (refreshCountDown) { this.setCountdown() } });
  }

  /**
 * Methode pour l'affichage du decompte
 * definiton du status d'affichage en fonction du temps restant.
 */
  setCountdown() {
    if (this.lilate) {
      const _sessionDiff = moment(this.lilate.end).diff(this.lilate.start); // interval de temps entre start vs end
      const sessionDuration = moment.duration(_sessionDiff); // la durée
      let halfTime = null; // 30 min
      // definiton du half time si > 1h
      if (sessionDuration.asHours() > 1) {
        const calc = sessionDuration.asHours() / 2;
        halfTime = moment(this.lilate.start).add(calc.toString(), 'hours')
      } else {
        halfTime = moment(this.lilate.start).add('15', 'minutes'); // si inférieur à 1h, on crée un object moment de 15min.
      }

      // Définition du status du decompte.
      const now = this.serverClock.nowMoment();
      let diff = moment(this.lilate.start).diff(now); // object moment entre le start vs now.
      this.duration = moment.duration(diff); // durée restant start vs now
      if (this.duration.asHours() < 0) {
        if (moment(now).isAfter(moment(this.lilate.end))) {
          this.countdownStatus = 'countdown-finish';
          diff = moment(now).diff(this.lilate.end);
          this.duration = moment.duration(diff);
        } else if (moment(now).isAfter(moment(halfTime))) {
          this.countdownStatus = 'countdown-from';
          diff = moment(now).diff(this.lilate.start);
          this.duration = moment.duration(diff);

        } else {
          this.countdownStatus = 'countdown-from';
          diff = moment(now).diff(this.lilate.start);
          this.duration = moment.duration(diff);
        }
      } else {
        this.countdownStatus = 'countdown-in';
      }
      //const labelCountDown = this.translateService.get(this.countdownStatus)
      if (Math.round(this.duration.asDays()) === 1) {
        this.countdown = Math.ceil(this.duration.asDays()) + ' ' + this.countDownLabelDay;
      }
      if (Math.ceil(this.duration.asDays()) > 1) {
        this.countdown = Math.ceil(this.duration.asDays()) + ' ' + this.countDownLabelDays;
      } else if (Math.floor(this.duration.asHours()) >= 1) {
        this.countdown = this.duration.hours() + ' ' + this.countDownLabelHours + ' ' + this.duration.minutes() + ' ' + this.countDownLabelMinutes;
      } else if (Math.floor(this.duration.asHours()) < 1) {
        this.countdown = this.duration.minutes() + ' min ' + this.duration.seconds() + 's';
      } else {
        this.countdown = Math.floor(this.duration.asHours()) + ' h ' + this.duration.minutes() + ' min ' + this.duration.seconds() + 's';
      }
    }
  }


  /**
   * Détermine si le niveau visé doit être affiché comme "Autres"
   * Retourne true si targeted_level est inférieur au niveau minimum de la langue
   */
  shouldShowOtherLevel(): boolean {
    if (!this.lilate?.targeted_level || !this.lilate?.language?.lilate_level) {
      return false;
    }

    const minLevel = this.lilate.language.lilate_level;
    const minCecrCode = this.cecrMapping[minLevel];
    
    // Convertir targeted_level en numérique pour comparer
    const targetedLevelNum = Object.keys(this.cecrMapping).find(
      key => this.cecrMapping[key] === this.lilate.targeted_level
    );

    // Si le niveau visé est inférieur au niveau minimum, afficher "Autres"
    return targetedLevelNum && parseInt(targetedLevelNum) < minLevel;
  }


}
